/**
 * Main menu and map select (TRD-09 §4).
 *
 * The two entry screens of the flow: MainMenu branches to the daily, to free play via
 * MapSelect, and to the unlock tree. MapSelect shows every authored map — locked ones
 * dimmed with the tree node that opens them named on the card, because a locked thing
 * that explains how to get it is content and a locked thing that doesn't is noise.
 *
 * Mini-maps are drawn from each map's buildMask on a tiny canvas: the layout IS the
 * map's identity, and 3 authored maps deserve better than three identical cards.
 */
import { useEffect, useRef, useState } from 'react';
import {
  CLASS_COLORS,
  CONTENT_HASH,
  UNLOCK_TREE,
  getTowerSafe,
  maps as MAPS,
  type MapDef,
} from '@prism/content';
import { SIM_VERSION, type BoardTowerSummary, type ClassId } from '@prism/sim';
import type { SaveData, Settings } from '../persistence/save.js';
import { shardsAvailable } from '../meta/meta.js';
import { bestWaveEver } from '../meta/meta.js';
import { MenuParticles } from './MenuParticles.js';
import { MenuVolume } from './MenuVolume.js';
import { RunPeek, type PeekTarget } from './RunPeek.js';

/** Shown top of the menu; bump per public drop. (CLIENT_VERSION in daily.ts is the
 *  submission-payload version — separate on purpose, it tracks the protocol.) */
export const GAME_VERSION = 'v077-alpha';

/** buildMask glyph → preview pixel. Same environment ramp as the game's CSS tokens. */
const MASK_COLORS: Record<string, string> = {
  '.': '#232327', // buildable ground
  '#': '#141416', // blocked
  p: '#3f3f46', // the path — the shape that IS the map
  s: '#4cc9f0', // spawn
  c: '#e8c95a', // the Core
};

export function MapThumb({
  map,
  board,
  scale = 4,
  className = 'map-thumb',
}: {
  map: MapDef;
  /** Towers to overlay — the leaderboard run peek draws a player's final board (B-22). */
  board?: readonly BoardTowerSummary[];
  scale?: number;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    canvas.width = map.width * scale;
    canvas.height = map.height * scale;
    for (let y = 0; y < map.height; y++) {
      const row = map.buildMask[y] ?? '';
      for (let x = 0; x < map.width; x++) {
        ctx.fillStyle = MASK_COLORS[row[x] ?? '.'] ?? MASK_COLORS['.'] ?? '#232327';
        ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
    for (const t of board ?? []) {
      const def = getTowerSafe(t.defId);
      const color = def ? CLASS_COLORS[def.classes[0] as ClassId].color : '#ffffff';
      ctx.fillStyle = color;
      ctx.fillRect(t.tileX * scale + 1, t.tileY * scale + 1, scale - 2, scale - 2);
      // Star pips: tiny white notches along the top edge, one per star above ★1.
      ctx.fillStyle = '#ffffff';
      for (let s = 1; s < t.star; s++) {
        ctx.fillRect(t.tileX * scale + 1 + s * 2, t.tileY * scale + 1, 1, 1);
      }
    }
  }, [map, board, scale]);
  return <canvas ref={ref} className={className} />;
}

/** The tree node that unlocks a map, so a locked card can say how to open it. */
function unlockNodeFor(mapId: string) {
  return UNLOCK_TREE.find((n) => n.payload.k === 'map' && n.payload.mapId === mapId);
}

interface MenuEntry {
  rank: number;
  playerId: string;
  displayName: string;
  score: number;
  wavesCleared: number;
  coreHp: number;
}

/**
 * Today's board, on the menu itself (playtest request) — the daily is a competition,
 * and a competition you have to click into is one nobody feels. Top rows plus your own
 * rank; every row opens the run's saved board and damage.
 */
/**
 * Warn when the deployed API is running a different simulation than this page.
 *
 * The server refuses a submission whose `simVersion` differs from its own (STALE_CLIENT)
 * — correctly, because the two would disagree about what the run did. But the refusal
 * happened at the END of a run, after the player had already spent twenty minutes on it,
 * and nothing said so. A version skew between the two deployed containers is the single
 * most likely reason for "the leaderboard never saves my runs", and it is answerable in
 * one request before a run is started rather than after one is lost.
 */
function VersionCheck() {
  const [server, setServer] = useState<{ simVersion: number; contentHash?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/health');
        if (!res.ok) return;
        const body = (await res.json()) as { simVersion?: number; contentHash?: string };
        if (!cancelled && typeof body.simVersion === 'number') {
          setServer({ simVersion: body.simVersion, ...(body.contentHash ? { contentHash: body.contentHash } : {}) });
        }
      } catch {
        // Unreachable is the board's story to tell, not this one's.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (server === null) return null;

  if (server.simVersion !== SIM_VERSION) {
    return (
      <p className="menu-board__note warn">
        ⚠ Server is on simulation v{server.simVersion}, this page is v{SIM_VERSION}. Runs will
        not count until they match — reload, and redeploy if that does not fix it.
      </p>
    );
  }

  // Same simulation version, different content: the two are generating different days
  // while both claiming to agree, which is the version check's blind spot and reaches the
  // player as SCORE_MISMATCH on a run they actually played.
  if (server.contentHash !== undefined && server.contentHash !== CONTENT_HASH) {
    return (
      <p className="menu-board__note warn">
        ⚠ Server and page agree on v{SIM_VERSION} but are running different content
        ({server.contentHash.slice(0, 8)} vs {CONTENT_HASH.slice(0, 8)}), so they generate
        different days and runs will not count. Redeploy both, or bump SIM_VERSION.
      </p>
    );
  }

  return null;
}

function MenuBoard({
  date,
  playerId,
  onPeek,
}: {
  date: string;
  playerId: string;
  onPeek: (target: PeekTarget) => void;
}) {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; entries: MenuEntry[]; around: number | null }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/leaderboard/${date}?limit=8&around=${encodeURIComponent(playerId)}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { entries: MenuEntry[]; around: number | null };
        if (!cancelled) setState({ status: 'ready', entries: body.entries, around: body.around });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, playerId]);

  /**
   * The frame is drawn in every state, and the state goes INSIDE it.
   *
   * The design only ever drew a full board, so loading, unreachable and empty each used
   * to replace the whole right column with one sentence floating in a third of the
   * screen — which reads as a broken page rather than as a quiet day.
   */
  const frame = (body: React.ReactNode, count?: string) => (
    <div className="menu-board">
      <div className="menu-board__head">
        <span className="menu-board__title">TODAY&rsquo;S BOARD</span>
        {count !== undefined && <span className="menu-board__count">{count}</span>}
      </div>
      <div className="menu-board__rule" />
      <div className="menu-board__head-row">
        <span>#</span>
        <span>PLAYER</span>
        <span>WAVE</span>
        <span>SCORE</span>
      </div>
      <div className="menu-board__rule menu-board__rule--dim" />
      {body}
    </div>
  );

  if (state.status === 'loading') {
    return frame(<p className="muted menu-board__note">loading board…</p>);
  }
  // The menu must never look broken because the API is asleep — say nothing loudly.
  if (state.status === 'error') {
    return frame(<p className="muted menu-board__note">leaderboard unreachable</p>);
  }
  if (state.entries.length === 0) {
    return frame(
      <>
        <VersionCheck />
        <p className="muted menu-board__note">No runs yet today — yours could be first.</p>
      </>,
    );
  }

  const ownRank = state.entries.find((e) => e.playerId === playerId)?.rank ?? state.around;

  return (
    <div className="menu-board">
      <div className="menu-board__head">
        <span className="menu-board__title">TODAY&rsquo;S BOARD</span>
        <span className="menu-board__count">
          {state.entries.length} run{state.entries.length === 1 ? '' : 's'} shown
        </span>
      </div>
      <div className="menu-board__rule" />
      <div className="menu-board__head-row">
        <span>#</span>
        <span>PLAYER</span>
        <span>WAVE</span>
        <span>SCORE</span>
      </div>
      <div className="menu-board__rule menu-board__rule--dim" />
      <VersionCheck />

      <div className="menu-board__rows">
        {state.entries.map((entry) => (
          <button
            key={entry.playerId}
            type="button"
            className={`menu-board__row${entry.playerId === playerId ? ' menu-board__row--me' : ''}`}
            title="View this run's board and damage"
            onClick={() =>
              onPeek({
                playerId: entry.playerId,
                displayName: entry.displayName,
                score: entry.score,
                wavesCleared: entry.wavesCleared,
                coreHp: entry.coreHp,
                rank: entry.rank,
                isMe: entry.playerId === playerId,
              })
            }
          >
            <span className="menu-board__rank">#{entry.rank}</span>
            <span className="menu-board__name">
              {entry.displayName || 'anonymous'}
              {entry.playerId === playerId ? ' (you)' : ''}
            </span>
            <span className="menu-board__waves">w{entry.wavesCleared}</span>
            <span className="menu-board__score">{entry.score.toLocaleString()}</span>
          </button>
        ))}
      </div>

      <div className="menu-board__foot">
        {ownRank !== null && ownRank > (state.entries[state.entries.length - 1]?.rank ?? 0) && (
          <span className="menu-board__you">YOU ARE #{ownRank}</span>
        )}
        <span className="menu-board__hint">click any run to open its board</span>
      </div>
    </div>
  );
}

interface MenuProps {
  save: SaveData;
  date: string;
  unlockedMaps: readonly string[];
  /** Public builds show only the daily and the leaderboard (see App's SHOW_EXTRAS). */
  showExtras: boolean;
  onDaily: () => void;
  onFreePlay: () => void;
  onTree: () => void;
  onBoard: () => void;
  onSettings: () => void;
  onDiscovery: () => void;
  /** Same writer the settings screen uses — see MenuVolume. */
  onChangeSettings: (patch: Partial<Settings>) => void;
}

export function MainMenu({
  save,
  date,
  unlockedMaps,
  showExtras,
  onDaily,
  onFreePlay,
  onTree,
  onBoard,
  onSettings,
  onChangeSettings,
  onDiscovery,
}: MenuProps) {
  const best = bestWaveEver(save);
  const [peek, setPeek] = useState<PeekTarget | null>(null);

  /**
   * The way in, in order of what this screen is for.
   *
   * Built as data rather than markup because the public build hides three of these
   * (SHOW_EXTRAS) and the design's numbering and stagger both have to close up behind
   * whatever is missing — hard-coded "02" on a button that is sometimes third is worse
   * than no numbering at all.
   */
  const actions: {
    key: string;
    label: string;
    note: string;
    onClick: () => void;
    primary?: boolean;
  }[] = [
    {
      key: 'daily',
      label: 'DAILY RUN',
      note: `${date} — everyone plays the same seed`,
      onClick: onDaily,
      primary: true,
    },
    { key: 'board', label: 'FULL LEADERBOARD', note: 'all past days', onClick: onBoard },
    // NOT behind `showExtras`. Discovery is where a player looks up the thing that just
    // killed them, which is worth most to someone who has played least — gating it behind
    // the same flag as the unlock tree would hide it from exactly them.
    {
      key: 'discovery',
      label: 'DISCOVERY',
      note: 'towers · enemies · bosses',
      onClick: onDiscovery,
    },
    ...(showExtras
      ? [
          {
            key: 'free',
            label: 'FREE PLAY',
            note: `${unlockedMaps.length} map${unlockedMaps.length === 1 ? '' : 's'} open`,
            onClick: onFreePlay,
          },
          {
            key: 'tree',
            label: 'UNLOCK TREE',
            note: `✦ ${shardsAvailable(save)} Shards to spend`,
            onClick: onTree,
          },
          { key: 'settings', label: 'SETTINGS', note: 'audio · feel · access', onClick: onSettings },
        ]
      : []),
  ];

  return (
    <div className="menu">
      <div className="menu__bloom" />
      <MenuParticles seed={1} />

      <div className="menu__left">
        <div className="menu__strip">
          <span className="menu__version">{GAME_VERSION}</span>
          <span className="menu__strip-rule" />
          <span>ROGUELIKE TOWER DEFENSE</span>
        </div>

        <div className="menu__mark">
          <div className="menu__mark-title">
            <span className="menu__mark-bar" />
            <span className="menu__mark-rule" />
            <h1 className="menu__title">PRISM</h1>
          </div>
          <p className="menu__tagline">
            Deterministic tower defense. Same seed, same run, every machine.
          </p>
        </div>

        <div className="menu__buttons">
          {actions.map((action, i) => (
            <button
              key={action.key}
              type="button"
              className={`menu__big${action.primary ? ' menu__big--primary' : ''}`}
              style={{ '--step': i } as React.CSSProperties}
              onClick={action.onClick}
            >
              <span className="menu__big-inner">
                {/* Decorative: the number and the chevron are the design's ordering cue,
                    not part of what the button is called. Left in the accessible name they
                    turn "Daily run" into "zero one daily run chevron". */}
                <span className="menu__big-index" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {action.primary ? (
                  <>
                    <span className="menu__big-stack">
                      <span className="menu__big-label">{action.label}</span>
                      <span className="menu__big-note">{action.note}</span>
                    </span>
                    <span className="menu__big-chevron" aria-hidden="true">
                      &rsaquo;
                    </span>
                  </>
                ) : (
                  <>
                    <span className="menu__big-label">{action.label}</span>
                    <span className="menu__big-note">{action.note}</span>
                  </>
                )}
              </span>
            </button>
          ))}
        </div>

        <div className="menu__foot">
          <span className="menu__foot-rule" />
          <MenuVolume settings={save.settings} onChange={onChangeSettings} />
          <p className="menu__stats">
            {save.stats.runsPlayed} runs · best wave <span className="menu__stat-hi">{best}</span>
            {showExtras && (
              <>
                {' · '}
                <span className="meta-shards">{save.shardsEarned} Shards earned</span>
              </>
            )}
          </p>
          <p className="menu__credit">FINKBYCASPER</p>
        </div>
      </div>

      <div className="menu__right">
        <div className="menu__right-edge" />
        <div className="menu__right-panel" />
        <div className="menu__right-inner">
          <MenuBoard date={date} playerId={save.playerId} onPeek={setPeek} />
        </div>
      </div>

      <div className="menu__vignette" />

      {peek && <RunPeek date={date} target={peek} onClose={() => setPeek(null)} />}
    </div>
  );
}

interface MapSelectProps {
  save: SaveData;
  unlockedMaps: readonly string[];
  onPick: (mapId: string) => void;
  onBack: () => void;
}

export function MapSelect({ save, unlockedMaps, onPick, onBack }: MapSelectProps) {
  const open = new Set(unlockedMaps);
  return (
    <div className="menu">
      <h1 className="menu__title menu__title--small">CHOOSE A MAP</h1>

      <div className="map-cards">
        {MAPS.map((map) => {
          const unlocked = open.has(map.id);
          const node = unlocked ? undefined : unlockNodeFor(map.id);
          const best = save.stats.bestWave[map.id] ?? 0;
          const wins = save.stats.winsPerMap[map.id] ?? 0;
          return (
            <button
              key={map.id}
              type="button"
              className={`map-card${unlocked ? '' : ' map-card--locked'}`}
              disabled={!unlocked}
              title={
                unlocked
                  ? `Play ${map.name}`
                  : node
                    ? `Locked — buy "${node.name}" in the Unlock Tree (✦${node.cost})`
                    : 'Locked'
              }
              onClick={() => onPick(map.id)}
            >
              <MapThumb map={map} />
              <b>{map.name}</b>
              {unlocked ? (
                <span className="muted">
                  {best > 0 ? `best wave ${best}` : 'never attempted'}
                  {wins > 0 ? ` · ${wins} win${wins > 1 ? 's' : ''}` : ''}
                </span>
              ) : (
                <span className="muted">
                  🔒 {node ? `"${node.name}" in the tree · ✦${node.cost}` : 'locked'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button type="button" onClick={onBack}>
        ← Back
      </button>
    </div>
  );
}
